import React from 'react';
import {
  Action, FormHandlers, Portal, useModal,
} from '@doveit/bricks';
import { Invoice } from '../../../providers/api/dtos';
import { InvoiceStatus } from '../../../domain/types';
import { useComputedInvoiceStatus } from '../../hooks/use-computed-invoice-status/useComputedInvoiceStatus';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import UpsertInvoice from '../upsert-invoice/UpsertInvoice';

export const UPDATE_INVOICE_TO_BILLED_ERROR_MESSAGE = 'Non è stato possibile aggiornare lo stato del pagamento';
export const UPDATE_INVOICE_TO_BILLED_SUCCESS_MESSAGE = 'Stato del pagamento aggiornato con successo';

interface ChildFnProps {
  markBilledInvoice: VoidFunction
}

export interface MarkBilledInvoiceActionProps {
  invoice: Invoice,
  onSuccess?: (invoiceToUpdate: Invoice) => void,
  children?: (props: ChildFnProps) => React.ReactNode
}

const MarkBilledInvoiceAction: React.FC<MarkBilledInvoiceActionProps> = ({
  invoice,
  onSuccess,
  children,
}) => {
  const [formActionDisabled, setFormActionDisabled] = React.useState(false);
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const { canBeUpdatedTo } = useComputedInvoiceStatus(invoice);

  const handleSuccess = React.useCallback((updatedInvoice: Invoice) => {
    modal.close();
    onSuccess?.(updatedInvoice);
  }, [modal, onSuccess]);

  if (!canBeUpdatedTo(InvoiceStatus.BILLED)) {
    return null;
  }

  return (
    <>
      {children?.({ markBilledInvoice: modal.open }) || (
        <Action
          label="Registra fattura"
          size="S"
          aria-label="Azione per registrare una fattura"
          onClick={() => modal.open()}
        />
      )}

      <Portal>
        <SimpleModal
          {...modal}
          title="Registra fattura"
          aria-label="Modale per registrare una fattura"
          footer={(
            <Action
              label="Registra fattura"
              color="primary"
              emphasis="high"
              disabled={formActionDisabled}
              aria-label="Azione per confermare la registrazione della fattura"
              onClick={() => formRef.current?.handleSubmit()}
            />
          )}
        >
          <UpsertInvoice
            formRef={formRef}
            invoice={invoice}
            toStatus={InvoiceStatus.BILLED}
            onSuccess={handleSuccess}
            onFormStatusChanged={setFormActionDisabled}
            disabledFields={{
              property: true,
              reference: true,
            }}
            hiddenFields={{
              paidValue: true,
            }}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default MarkBilledInvoiceAction;
